import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, Store, Calendar, Handshake, Plus, RefreshCw, ChevronLeft } from 'lucide-react';

/**
 * UpdateCard — single entry in the home-page activity feed.
 *
 * Generous, image-forward, with clear hierarchy: avatar + entity-type
 * badge + relative time, big title, zone chips, and a quiet "view"
 * affordance that fades in on hover.
 */
const ATTRIBUTE_NAMES = {
    business_description: 'תיאור',
    business_name:        'שם העסק',
    business_phone:       'טלפון',
    business_address:     'כתובת',
    business_hours:       'שעות פעילות',
    business_category:    'קטגוריה',
    business_website:     'אתר',
    business_email:       'אימייל',
    thumbnail:            'תמונה',
    images:               'תמונות',
};

const TYPE_BADGE = {
    business: { Icon: Store,     label: 'עסק',    tone: 'info' },
    event:    { Icon: Calendar,  label: 'מפגש',   tone: 'brand' },
    barter:   { Icon: Handshake, label: 'בארטר',  tone: 'success' },
};

const TONE_CLASS = {
    info:    'bg-info-50 text-info-700 ring-info-100',
    brand:   'bg-brand-50 text-brand-700 ring-brand-200',
    success: 'bg-success-50 text-success-700 ring-success-100',
};

function formatTimestamp(ts) {
    try {
        const date = new Date(parseInt(ts));
        const now = new Date();
        const diffMs = now - date;
        const diffH = Math.floor(diffMs / 3.6e6);
        const diffD = Math.floor(diffH / 24);
        if (diffH < 1) {
            const m = Math.max(1, Math.floor(diffMs / 6e4));
            return `לפני ${m} ${m === 1 ? 'דקה' : 'דקות'}`;
        }
        if (diffH < 24) return `לפני ${diffH} ${diffH === 1 ? 'שעה' : 'שעות'}`;
        if (diffD === 1) return 'אתמול';
        if (diffD < 7) return `לפני ${diffD} ימים`;
        const w = Math.floor(diffD / 7);
        return `לפני ${w} ${w === 1 ? 'שבוע' : 'שבועות'}`;
    } catch {
        return '';
    }
}

const UpdateCard = ({ update, onClick, zones }) => {
    const [imageError, setImageError] = useState(false);

    const action = update.action;
    const isCreate = action === 'created';
    const ActionIcon = isCreate ? Plus : RefreshCw;
    const actionLabel = isCreate ? 'נוסף' : 'עודכן';

    const typeBadge = TYPE_BADGE[update.type] || null;

    const updatedAttrs = update.updated_attributes && update.updated_attributes.length > 0
        ? update.updated_attributes.map((a) => ATTRIBUTE_NAMES[a] || a)
        : null;

    const getZoneName = (zoneId) => {
        const zone = zones?.find((z) => z.zone_id === zoneId);
        return zone?.zone_display_name || zone?.zone_id || zoneId;
    };

    const zoneDisplayList = (update.zones && update.zones.length > 0)
        ? update.zones.map((z) => typeof z === 'string' ? { id: z, label: getZoneName(z) } : { id: z.zone_id, label: z.zone_display_name || z.zone_id })
        : [{ id: update.zone_id, label: getZoneName(update.zone_id) }];

    return (
        <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            whileTap={{ scale: 0.995 }}
            onClick={onClick}
            dir="rtl"
            className="group relative w-full text-right block bg-surface ring-1 ring-line-subtle hover:ring-line first:rounded-t-2xl last:rounded-b-2xl -mt-px first:mt-0 transition-[background-color,box-shadow] duration-200 hover:bg-surface-muted/40 focus:outline-none focus-visible:bg-surface-muted/60 focus-visible:ring-brand-300"
        >
            <div className="flex items-start gap-3.5 p-4 sm:p-5">
                {/* Avatar */}
                <div className="relative shrink-0">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-pill overflow-hidden ring-2 ring-white shadow-sm bg-gradient-to-br from-brand-100 to-brand-200">
                        {update.thumbnail && !imageError ? (
                            <img
                                src={update.thumbnail}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={() => setImageError(true)}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-brand-600" strokeWidth={2} />
                            </div>
                        )}
                    </div>
                    {/* Action dot — tiny + or refresh badge */}
                    <span
                        aria-hidden="true"
                        className={`absolute -bottom-0.5 -end-0.5 inline-flex items-center justify-center w-5 h-5 rounded-pill ring-2 ring-surface ${isCreate ? 'bg-success-500' : 'bg-info-500'} text-white`}
                    >
                        <ActionIcon className="w-2.5 h-2.5" strokeWidth={3} />
                    </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
                        <span className="font-bold text-ink leading-tight">{update.name}</span>
                        <span className="text-sm text-ink-muted">{actionLabel}</span>
                        {typeBadge && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-2xs font-bold ring-1 ${TONE_CLASS[typeBadge.tone]}`}>
                                <typeBadge.Icon className="w-3 h-3" strokeWidth={2.5} />
                                {typeBadge.label}
                            </span>
                        )}
                    </div>

                    {/* Updated attributes line */}
                    {updatedAttrs && (
                        <p className="text-xs text-ink-muted leading-relaxed mb-1.5 truncate">
                            {updatedAttrs.join(' · ')}
                        </p>
                    )}

                    {/* Meta row: zone chips + timestamp */}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            {zoneDisplayList.map((z) => (
                                <span
                                    key={z.id}
                                    className="inline-flex items-center gap-1 text-2xs font-medium text-brand-700 bg-brand-50/70 ring-1 ring-brand-100 rounded-pill px-2 py-0.5"
                                >
                                    <MapPin className="w-2.5 h-2.5" strokeWidth={2.25} />
                                    {z.label}
                                </span>
                            ))}
                        </div>
                        <span className="text-2xs text-ink-subtle whitespace-nowrap">{formatTimestamp(update.timestamp)}</span>
                    </div>
                </div>

                {/* Trailing chevron — fades in on hover */}
                <ChevronLeft
                    aria-hidden="true"
                    className="shrink-0 self-center w-5 h-5 text-ink-faint opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform,color] duration-300 ease-smooth group-hover:text-brand-700"
                    strokeWidth={2.5}
                />
            </div>
        </motion.button>
    );
};

export default UpdateCard;
